'use client'

import { useSyncMachineWithArtBlocksClient } from '@/hooks/use-sync-machine-with-artblocks-client'
import {
  deserializeSnapshot,
  projectSaleManagerMachine
} from '@artblocks/sdk/dist/machines/project-sale-manager-machine'
import { purchaseInitiationMachine } from '@artblocks/sdk/dist/machines/purchase-initiation-machine'
import { purchaseTrackingMachine } from '@artblocks/sdk/dist/machines/purchase-tracking-machine'
import { ramMachine } from '@artblocks/sdk/dist/machines/ram-machine'
import { postRAMMachine } from '@artblocks/sdk/dist/machines/post-ram-machine'
import { ActorRefFrom, SnapshotFrom, createEmptyActor } from '@artblocks/sdk/dist/machines/utils'
import { useArtBlocksClient } from '@components/artblocks-provider'
import { purchaseTrackingManagerMachineContext } from '@components/purchase-tracking-manager-machine-context'
import { createActorContext, useSelector } from '@xstate/react'
import { createContext, ReactElement, useCallback, useContext, useEffect, useState } from 'react'
import { Hex } from 'viem'

export const ProjectSaleManagerMachineContext = createActorContext(projectSaleManagerMachine)

export function ProjectSaleManagerMachineContextProvider({
  projectId,
  snapshot,
  children
}: {
  projectId?: string
  snapshot?: string | null
  children: React.ReactNode
}): ReactElement {
  const artblocksClient = useArtBlocksClient()
  const deserializedSnapshot = snapshot
    ? deserializeSnapshot({
        snapshot: JSON.parse(snapshot ?? '{}'),
        artblocksClient
      })
    : undefined

  return (
    <ProjectSaleManagerMachineContext.Provider
      options={{
        snapshot: deserializedSnapshot,
        input: {
          projectId,
          artblocksClient: artblocksClient
        }
      }}
    >
      <PurchaseTrackingMachineProvider>
        <ProjectSaleManagerMachineManager />
        {children}
      </PurchaseTrackingMachineProvider>
    </ProjectSaleManagerMachineContext.Provider>
  )
}

function ProjectSaleManagerMachineManager() {
  const projectSaleManagerMachineRef = ProjectSaleManagerMachineContext.useActorRef()
  useSyncMachineWithArtBlocksClient(projectSaleManagerMachineRef)

  const purchaseTrackingManagerMachineRef = purchaseTrackingManagerMachineContext.useActorRef()
  useEffect(() => {
    projectSaleManagerMachineRef.send({
      type: 'PURCHASE_TRACKING_MANAGER_MACHINE_AVAILABLE',
      data: purchaseTrackingManagerMachineRef as any
    })
  }, [purchaseTrackingManagerMachineRef, projectSaleManagerMachineRef])

  return null
}

function stringifyWithBigInts(value: any) {
  return JSON.stringify(value, (_, v) => (typeof v === 'bigint' ? v.toString() : v))
}

export function useLiveSaleData() {
  const liveSaleData = ProjectSaleManagerMachineContext.useSelector(
    (state) => {
      return state.context.liveSaleData
    },
    (a, b) => {
      return stringifyWithBigInts(a) === stringifyWithBigInts(b)
    }
  )

  return liveSaleData
}

export function useRAMMachine<T = SnapshotFrom<typeof ramMachine>>(
  selector?: (state: SnapshotFrom<typeof ramMachine>) => T
): { state: T | undefined; ref: ActorRefFrom<typeof ramMachine> | undefined } {
  const ramMachineRef = ProjectSaleManagerMachineContext.useSelector((state) => state.context.ramMachine)

  // Use the selector within useSelector for selective re-renders
  const selectedState = useSelector(ramMachineRef ?? createEmptyActor(), (state) => {
    if ('value' in state) {
      return selector ? selector(state) : (state as T) // Cast to T if no selector is provided
    }
    return undefined
  })

  if (!ramMachineRef) {
    return {
      state: undefined,
      ref: undefined
    }
  }

  return {
    state: selectedState,
    ref: ramMachineRef
  }
}

export function usePostRAMMachine<T = SnapshotFrom<typeof postRAMMachine>>(
  selector?: (state: SnapshotFrom<typeof postRAMMachine>) => T
): { state: T | undefined; ref: ActorRefFrom<typeof postRAMMachine> | undefined } {
  const postRAMMachineRef = ProjectSaleManagerMachineContext.useSelector((state) => state.context.postRAMMachine)

  // Use the selector within useSelector for selective re-renders
  const selectedState = useSelector(postRAMMachineRef ?? createEmptyActor(), (state) => {
    if ('value' in state) {
      return selector ? selector(state) : (state as T) // Cast to T if no selector is provided
    }
    return undefined
  })

  if (!postRAMMachineRef) {
    return {
      state: undefined,
      ref: undefined
    }
  }

  return {
    state: selectedState,
    ref: postRAMMachineRef
  }
}

export function usePurchaseInitiationMachine():
  | {
      state: SnapshotFrom<typeof purchaseInitiationMachine>
      ref: ActorRefFrom<typeof purchaseInitiationMachine>
    }
  | { state: undefined; ref: undefined } {
  const purchaseInitiationMachineRef = ProjectSaleManagerMachineContext.useSelector(
    (state) => state.context.purchaseInitiationMachine
  )

  const purchaseInitiationMachineState = useSelector(purchaseInitiationMachineRef ?? createEmptyActor(), (state) => {
    if ('value' in state) {
      return state
    }

    return null
  })

  if (!purchaseInitiationMachineState || !purchaseInitiationMachineRef) {
    return {
      state: undefined,
      ref: undefined
    }
  }

  return {
    state: purchaseInitiationMachineState,
    ref: purchaseInitiationMachineRef
  }
}

type PurchaseTrackingMachineContextType =
  | {
      state: SnapshotFrom<typeof purchaseTrackingMachine> | undefined
      ref: ActorRefFrom<typeof purchaseTrackingMachine> | undefined
      reset: () => void
    }
  | {
      state: undefined
      ref: undefined
      reset: undefined
    }

const PurchaseTrackingMachineContext = createContext<PurchaseTrackingMachineContextType | undefined>(undefined)

export function PurchaseTrackingMachineProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const { ref: purchaseInitiationMachineRef } = usePurchaseInitiationMachine()
  const [purchaseTxHash, setPurchaseTxHash] = useState<Hex | undefined>()

  const reset = useCallback(() => {
    setPurchaseTxHash(undefined)
  }, [])

  useEffect(() => {
    purchaseInitiationMachineRef?.on('purchaseInitiated', (event) => {
      setPurchaseTxHash(event.txHash)
    })
  }, [purchaseInitiationMachineRef])

  const purchaseTrackingMachineRef = purchaseTrackingManagerMachineContext.useSelector(
    (state) => {
      return state.context.purchaseTrackingMachines[purchaseTxHash ?? '']
    },
    (a, b) => {
      return a && b && Object.keys(a).join() === Object.keys(b).join()
    }
  )

  const purchaseTrackingMachineState = useSelector(purchaseTrackingMachineRef ?? createEmptyActor(), (state) => {
    if ('value' in state) {
      return state
    }
    return undefined
  })

  const contextValue: PurchaseTrackingMachineContextType = {
    state: purchaseTrackingMachineState,
    ref: purchaseTrackingMachineRef,
    reset
  }

  return (
    <PurchaseTrackingMachineContext.Provider value={contextValue}>{children}</PurchaseTrackingMachineContext.Provider>
  )
}

export function usePurchaseTrackingMachine(): PurchaseTrackingMachineContextType {
  const context = useContext(PurchaseTrackingMachineContext)
  if (context === undefined) {
    throw new Error('usePurchaseTrackingMachine must be used within a PurchaseTrackingMachineProvider')
  }
  return context
}
